import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * MobileHeaderDropdown - Clickable header title with dropdown for dashboard navigation
 * Replaces static title on mobile with an interactive dropdown selector
 */
const MobileHeaderDropdown = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Determine which dashboards the user has access to
  const getAvailableDashboards = () => {
    const dashboards = [];
    const role = profile?.role;

    if (['player', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/player', label: 'Player Dashboard' });
    }
    if (['parent', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/parent', label: 'Parent Dashboard' });
    }
    if (['coach', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/coach', label: 'Coach Dashboard' });
    }
    if (['admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/admin', label: 'Admin Dashboard' });
    }

    return dashboards;
  };

  const availableDashboards = getAvailableDashboards();
  const currentDashboard = availableDashboards.find((d) => d.path === location.pathname);

  // If user only has access to one dashboard, just show static title
  if (availableDashboards.length <= 1) {
    return (
      <h1
        style={{
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#333',
          margin: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {currentDashboard?.label || 'Dashboard'}
      </h1>
    );
  }

  const styles = {
    container: {
      position: 'relative',
      flex: 1,
      zIndex: 1001,
    },
    headerButton: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
      padding: '8px 12px',
      backgroundColor: 'white',
      border: '2px solid #007bff',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      minHeight: '44px',
      width: '100%',
      textAlign: 'left',
      overflow: 'hidden',
    },
    titleText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
    },
    chevron: {
      fontSize: '14px',
      transition: 'transform 0.2s',
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      flexShrink: 0,
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '2px solid #007bff',
      borderTop: 'none',
      borderRadius: '0 0 6px 6px',
      marginTop: '-2px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 10000,
      overflow: 'hidden',
    },
    dropdownItem: {
      padding: '14px 16px',
      fontSize: '16px',
      fontWeight: '500',
      backgroundColor: 'white',
      color: '#333',
      border: 'none',
      borderBottom: '1px solid #eee',
      cursor: 'pointer',
      width: '100%',
      textAlign: 'left',
      minHeight: '44px',
      display: 'block',
    },
    dropdownItemActive: {
      backgroundColor: '#e3f2fd',
      color: '#007bff',
      fontWeight: '600',
    },
  };

  return (
    <div style={styles.container}>
      <button type="button" style={styles.headerButton} onClick={() => setIsOpen(!isOpen)}>
        <span style={styles.titleText}>{currentDashboard?.label || 'Dashboard'}</span>
        <span style={styles.chevron}>▼</span>
      </button>
      {isOpen && (
        <div style={styles.dropdown} onClick={(e) => e.stopPropagation()}>
          {availableDashboards.map((dashboard) => {
            const isActive = location.pathname === dashboard.path;
            return (
              <button
                key={dashboard.path}
                type="button"
                style={{
                  ...styles.dropdownItem,
                  ...(isActive ? styles.dropdownItemActive : {}),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(dashboard.path);
                  setIsOpen(false);
                }}
              >
                {dashboard.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileHeaderDropdown;
